import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import SEOHelmet from "@/components/SEOHelmet";
import { useGA4Analytics } from "@/hooks/useGA4Analytics";
import { AnalyticsOverviewCards } from "@/components/admin/analytics/AnalyticsOverviewCards";
import { TrafficSourcesChart } from "@/components/admin/analytics/TrafficSourcesChart";
import { DailyMetricsChart } from "@/components/admin/analytics/DailyMetricsChart";
import { ConversionFunnelChart } from "@/components/admin/analytics/ConversionFunnelChart";
import { UserJourneyFlow } from "@/components/admin/analytics/UserJourneyFlow";
import { GoalProgressCards } from "@/components/admin/analytics/GoalProgressCards";
import { EventsLiveTable } from "@/components/admin/analytics/EventsLiveTable";
import { PagePerformanceTable } from "@/components/admin/analytics/PagePerformanceTable";
import { LiveUsersWidget } from "@/components/admin/analytics/LiveUsersWidget";
import { CohortRetentionTable } from "@/components/admin/analytics/CohortRetentionTable";
import { CohortConversionChart } from "@/components/admin/analytics/CohortConversionChart";
import { CohortSegmentComparison } from "@/components/admin/analytics/CohortSegmentComparison";
import { CohortInsights } from "@/components/admin/analytics/CohortInsights";
import { useCohortAnalysis } from "@/hooks/useCohortAnalysis";
import { useCohortInsights } from "@/hooks/useCohortInsights";
import { Button } from "@/components/ui/button";
import { Calendar, Activity, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AnalyticsDashboard() {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const [cohortGranularity, setCohortGranularity] = useState<'weekly' | 'monthly'>('weekly');
  const [cohortSegment, setCohortSegment] = useState<'all' | 'practice_type' | 'source' | 'device'>('practice_type');

  const { data, isLoading, error } = useGA4Analytics(dateRange);
  
  const { data: cohortData, isLoading: cohortLoading } = useCohortAnalysis({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    granularity: cohortGranularity,
    segment: cohortSegment
  });

  const insights = useCohortInsights({
    cohorts: cohortData?.cohorts || [],
    segments: cohortData?.segments || []
  });

  const handleLast7Days = () => {
    setDateRange({
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    });
  };

  const handleLast30Days = () => {
    setDateRange({
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    });
  };

  const handleLast90Days = () => {
    setDateRange({
      startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    });
  };

  return (
    <AdminLayout>
      <SEOHelmet 
        title="Analytics Dashboard - Admin"
        description="Google Analytics 4 Dashboard mit Echtzeit-Metriken"
      />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground">
              Umfassende Analytics mit Conversion Tracking & User Journeys
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLast7Days}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Letzte 7 Tage
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLast30Days}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Letzte 30 Tage
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLast90Days}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Letzte 90 Tage
            </Button>
          </div>
        </div>

        {error && (
          <Alert className="border-muted bg-muted/30">
            <AlertDescription className="text-sm">
              <span className="font-medium">GA4-Integration nicht verfügbar</span>
              <span className="text-xs text-muted-foreground ml-2">
                Die Custom-Analytics funktionieren unabhängig davon.
              </span>
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="conversions">Conversions</TabsTrigger>
            <TabsTrigger value="cohorts">
              <Users className="mr-2 h-4 w-4" />
              Kohorten
            </TabsTrigger>
            <TabsTrigger value="events">
              <Activity className="mr-2 h-4 w-4" />
              Live Events
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Live Users Widget */}
            <LiveUsersWidget />

            {/* Goal Progress Cards */}
            <GoalProgressCards startDate={dateRange.startDate} endDate={dateRange.endDate} />

            {/* GA4 Overview Cards (if available) */}
            {isLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-[120px]" />
                ))}
              </div>
            ) : data ? (
              <AnalyticsOverviewCards overview={data.overview} />
            ) : null}

            {/* Daily Metrics & Traffic Sources */}
            <div className="grid gap-6 lg:grid-cols-2">
              {isLoading ? (
                <>
                  <Skeleton className="h-[400px]" />
                  <Skeleton className="h-[400px]" />
                </>
              ) : data ? (
                <>
                  <DailyMetricsChart metrics={data.dailyMetrics} />
                  <TrafficSourcesChart sources={data.trafficSources} />
                </>
              ) : null}
            </div>

            {/* Page Performance */}
            <PagePerformanceTable startDate={dateRange.startDate} endDate={dateRange.endDate} />
          </TabsContent>

          <TabsContent value="conversions" className="space-y-6">
            {/* Conversion Funnel */}
            <ConversionFunnelChart startDate={dateRange.startDate} endDate={dateRange.endDate} />

            {/* User Journey Flow */}
            <UserJourneyFlow startDate={dateRange.startDate} endDate={dateRange.endDate} />
          </TabsContent>

          <TabsContent value="cohorts" className="space-y-6">
            {/* Cohort Controls */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Granularität:</span>
                <Select value={cohortGranularity} onValueChange={(v) => setCohortGranularity(v as 'weekly' | 'monthly')}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Wöchentlich</SelectItem>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Segment:</span>
                <Select value={cohortSegment} onValueChange={(v) => setCohortSegment(v as any)}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="practice_type">Praxistyp</SelectItem>
                    <SelectItem value="source">Traffic-Quelle</SelectItem>
                    <SelectItem value="device">Gerätetyp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Cohort Insights */}
            <CohortInsights insights={insights} />

            {/* Retention Heatmap */}
            <CohortRetentionTable 
              cohorts={cohortData?.cohorts || []} 
              isLoading={cohortLoading}
            />

            {/* Conversion & Segments */}
            <div className="grid gap-6 lg:grid-cols-2">
              <CohortConversionChart 
                cohorts={cohortData?.cohorts || []} 
                isLoading={cohortLoading}
              />
              {cohortSegment !== 'all' && (
                <CohortSegmentComparison 
                  segments={cohortData?.segments || []} 
                  isLoading={cohortLoading}
                  segmentType={cohortSegment}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="events" className="space-y-6">
            {/* Live Events Table */}
            <EventsLiveTable limit={50} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
